import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Copy, Check, Download } from 'lucide-react';

export function buildSnippet({ snippetTag, label, title, narrative, keyPoints, disclaimer }) {
  const now = new Date();
  const ts = now.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const divider = '---';
  const lines = [
    `[${snippetTag}] ${label}`,
    ts,
    divider,
  ];
  if (title) lines.push(`${title}`, '');
  if (narrative) lines.push(narrative, '');
  if (keyPoints && keyPoints.length > 0) {
    keyPoints.forEach(p => lines.push(`  - ${p}`));
    lines.push('');
  }
  lines.push(divider);
  lines.push(disclaimer || 'This is not a certified valuation. All figures are directional indicators for strategic planning purposes only.');
  lines.push('', 'Source: YBV Brand Intelligence Platform');
  return lines.join('\n');
}

export function SharePopover({ snippet, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = snippet;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([snippet], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ybv-insight-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-3 rounded-lg border border-border/50 bg-muted/30 overflow-hidden" data-testid="share-popover">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border/30 bg-muted/50">
        <span className="text-2xs font-medium text-muted-foreground tracking-wide uppercase">Share-ready snippet</span>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" className="h-6 px-2 text-2xs gap-1" onClick={handleCopy} data-testid="share-copy-btn">
            {copied ? <Check className="w-3 h-3 text-stable" /> : <Copy className="w-3 h-3" />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button variant="ghost" size="sm" className="h-6 px-2 text-2xs gap-1" onClick={handleDownload} data-testid="share-download-btn">
            <Download className="w-3 h-3" /> .txt
          </Button>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground text-xs px-1">&times;</button>
        </div>
      </div>
      <pre className="p-3 text-xs text-foreground/80 whitespace-pre-wrap font-mono leading-relaxed max-h-52 overflow-y-auto" data-testid="share-snippet-text">
        {snippet}
      </pre>
    </div>
  );
}
